import React from 'react';
import {
  Menu,
  MenuItem,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  CssBaseline,
  Stack,
  Avatar
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaidIcon from '@mui/icons-material/Paid';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HomeIcon from '@mui/icons-material/Home';
import { useTheme } from '@mui/material/styles';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import IconButton from '@mui/material/IconButton';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const Layout = ({ children }) => {
  const location = useLocation();
  const theme = useTheme();
  const drawerWidth = theme.spacing(28); // 28 * 8px = 224px
  const collapsedWidth = theme.spacing(8); // 8 * 8px = 64px
  const [open, setOpen] = React.useState(true);

  const routes = [
    { label: 'Dashboard', path: '/', icon: <DashboardIcon /> },
    { label: 'Products', path: '/products', icon: <InventoryIcon /> },
    { label: 'Sales', path: '/sales', icon: <ShoppingCartIcon /> },
    { label: 'Customers', path: '/customers', icon: <PeopleIcon /> },
    { label: 'Expenses', path: '/expenses', icon: <PaidIcon /> },
    { label: 'Transactions', path: '/transactions', icon: <ReceiptIcon /> },
    { label: 'Daily Analysis', path: '/daily-analysis', icon: <TrendingUpIcon /> },
    { label: 'Analytics', path: '/analytics', icon: <AnalyticsIcon /> },
    { label: 'Profit Reports', path: '/profit-reports', icon: <AssessmentIcon /> }
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <Box>
        <AppBar
          position="fixed"
          color="inherit"
          elevation={0}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            background: 'rgba(255,255,255,0.95)',
            borderBottom: '1px solid #e0e0e0'
          }}>
          <Toolbar sx={{ minHeight: 64 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{ flexGrow: 1, color: 'primary.main', fontWeight: 700, letterSpacing: 1 }}>
              POS System
            </Typography>
            <LoggedInUserName />
          </Toolbar>
        </AppBar>
      </Box>
      <Box>
        <Drawer
          variant="permanent"
          open={open}
          sx={{
            width: open ? drawerWidth : collapsedWidth,
            flexShrink: 0,
            mt: '64px', // Offset Drawer below AppBar
            [`& .MuiDrawer-paper`]: {
              width: open ? drawerWidth : collapsedWidth,
              boxSizing: 'border-box',
              background: 'linear-gradient(180deg, #f5f7fa 60%, #e3eafc 100%)',
              borderRight: '1px solid #e0e0e0',
              boxShadow: '2px 0 8px 0 rgba(60,60,60,0.04)',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen
              }),
              top: '64px', // Offset Drawer paper below AppBar
              height: 'calc(100% - 64px)'
            }
          }}>
          <Toolbar
            sx={{
              minHeight: 64,
              justifyContent: open ? 'flex-start' : 'center',
              px: 1,
              width: '100%'
            }}>
            <IconButton
              onClick={() => setOpen((prev) => !prev)}
              edge="start"
              size="large"
              sx={{
                mr: open ? 1 : 0,
                background: open ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.15)',
                color: 'primary.main',
                borderRadius: 2,
                boxShadow: open ? 1 : 2,
                transition: 'background 0.2s, box-shadow 0.2s',
                '&:hover': {
                  background: 'rgba(25, 118, 210, 0.18)'
                }
              }}
              aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}>
              {open ? <ChevronLeftIcon fontSize="medium" /> : <MenuOpenIcon fontSize="medium" />}
            </IconButton>
            {open && (
              <Typography
                variant="h6"
                color="primary"
                sx={{ fontWeight: 700, letterSpacing: 1, ml: 1 }}>
                POS
              </Typography>
            )}
          </Toolbar>
          <Box sx={{ overflow: 'auto', mt: 2 }}>
            <List>
              <ListItem disablePadding sx={{ justifyContent: open ? 'initial' : 'center' }}>
                <ListItemButton
                  component={RouterLink}
                  to="/"
                  selected={location.pathname === '/'}
                  sx={{
                    borderRadius: 2,
                    mx: 1,
                    my: 0.5,
                    justifyContent: open ? 'initial' : 'center',
                    px: open ? 2 : 1
                  }}>
                  <ListItemIcon
                    sx={{ minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center' }}>
                    <HomeIcon color={location.pathname === '/' ? 'primary' : 'inherit'} />
                  </ListItemIcon>
                  {open && <ListItemText primary="Home" />}
                </ListItemButton>
              </ListItem>
              {routes
                .filter((r) => r.path !== '/')
                .map((route) => (
                  <ListItem
                    key={route.path}
                    disablePadding
                    sx={{ justifyContent: open ? 'initial' : 'center' }}>
                    <ListItemButton
                      component={RouterLink}
                      to={route.path}
                      selected={location.pathname === route.path}
                      sx={{
                        borderRadius: 2,
                        mx: 1,
                        my: 0.5,
                        justifyContent: open ? 'initial' : 'center',
                        px: open ? 2 : 1
                      }}>
                      <ListItemIcon
                        sx={{ minWidth: 0, mr: open ? 2 : 'auto', justifyContent: 'center' }}>
                        {React.cloneElement(route.icon, {
                          color: location.pathname === route.path ? 'primary' : 'inherit'
                        })}
                      </ListItemIcon>
                      {open && <ListItemText primary={route.label} />}
                    </ListItemButton>
                  </ListItem>
                ))}
            </List>
          </Box>
        </Drawer>
      </Box>
      <Box component="main" sx={{ flex: 1, minWidth: 0 }}>
        <Toolbar sx={{ minHeight: 64 }} />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4, pl: 0, pr: 0 }}>
          {children}
        </Container>
      </Box>
    </Box>
  );
};

const getUserName = () => {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) return '';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.username || '';
  } catch {
    return '';
  }
};

const LoggedInUserName = () => {
  try {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const username = getUserName();
    if (!username) return null;

    const open = Boolean(anchorEl);

    const handleMenu = (event) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleLogout = () => {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
    };

    return (
      <>
        {username && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar
              sx={{ bgcolor: 'primary.main', width: 32, height: 32, cursor: 'pointer' }}
              onClick={handleMenu}>
              {username[0]?.toUpperCase()}
            </Avatar>
            <Typography variant="body2" fontWeight={500} color="text.primary">
              {username}
            </Typography>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              <MenuItem
                onClick={() => {
                  handleLogout();
                  handleClose();
                }}>
                <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </Stack>
        )}
      </>
    );
  } catch {
    return null;
  }
};

export default Layout;
